// Copyright (c) 2023 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import { StatusCode } from "grpc-web"
import { t } from "@lingui/macro"
import * as Actions from "../actions"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import { grpc } from "../../../app/rpc/executor"
import { assistantClient } from "../../../app/rpc/client"
import { showSnackBar } from "../../snackbar/actions"
import { isGrpcError } from "../../../lib/rpcutils"
import {
  GenerateNarrativeRequest,
  GenerateNarrativeResponse,
} from "../../../generated/wisetime/user/assistant/v4/assistant_pb"

type GenerateNarrativeRpc = (request: GenerateNarrativeRequest) => Observable<GenerateNarrativeResponse>

export const generateNarrative = (generateNarrativeRpc: GenerateNarrativeRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.generateNarrative.match),
      mergeMap(action => of(action.payload)
        .pipe(
          mergeMap(generateNarrativeRpc),
          mergeMap(response => {
            const [narrative] = response.toObject().narrativeList
            if (narrative) {
              return of(TimelineAdditionalUiSlice.setPendingNarratives([
                { key: action.payload.getUnpostedTimeGroupId(), text: narrative },
              ]))
            }
            return of(TimelineAdditionalUiSlice.deletePendingNarratives([action.payload.getUnpostedTimeGroupId()]))
          }),
          catchError((e) => handleError(e, action.payload)),
        )
      ),
    )

const handleError = (e: unknown, payload: GenerateNarrativeRequest): Observable<Action> => {
  const actions: Action[] = [
    TimelineAdditionalUiSlice.deletePendingNarratives([payload.getUnpostedTimeGroupId()]),
  ]
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.PERMISSION_DENIED) {
      actions.push(showSnackBar({
        message: t({ id: "msg_generate_narrative_permission_denied", message: "Failed to generate narrative. You do not have permission." }),
        messageType: "ERROR",
      }))
      return concat(actions)
    }
    if (code === StatusCode.NOT_FOUND) {
      actions.push(
        showSnackBar({
          message: t({ id: "msg_generate_narrative_not_found", message: "Failed to generate narrative. Could not find time group." }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      actions.push(
        showSnackBar({
          message: t({ id: "msg_generate_narrative_failed_precondition", message: "Failed to generate narrative. Time group has already been posted." }),
          messageType: "ERROR"
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
  }
  actions.push(showSnackBar({
    message: t({ id: "msg_generate_narrative_failed", message: "Failed to generate narrative. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
  return concat(actions)
}

const generateNarrativeRpc = (request: GenerateNarrativeRequest): Observable<GenerateNarrativeResponse> =>
  grpc(() => assistantClient.generateNarrative(request, null))

export default generateNarrative(generateNarrativeRpc)
